<template>
  <draggable
    v-if="!!modelValue"
    :list="modelValue"
    class="tour"
    draggable=".tour-day-container:not(.not-draggable)"
    item-key="id"
    v-bind="dragOptions"
    @change="change"
  >
    <template #item="{ element, index }">
      <div
        :key="index"
        :class="{
          'not-draggable ghost-item': updatingTour,
          'not-draggable blocked-item': element.isBlocked  || isSold,
        }"
        class="tour-day-container"
      >
        <div key="tour-day">
          <div v-if="index !== 0" class="w-100 d-flex justify-content-center add-day-wrapper">
            <v-button
              :disabled="updatingTour || element.isBlocked || isSold"
              class="btn-add-plus rounded-circle shadow-sm mb-4 btn-add-day-hover"
              size="xs"
              variant="primary"
              @click="addDay(element.order)"
              title="Add Day"
              style="transition: all 0.2s;"
            >
              <v-icon icon="plus" style="font-size: 10px; line-height: 1;" />
            </v-button>
          </div>

          <div :class="{ 'mt-12': index === 0 }" class="tour-day d-flex flex-row row gx-0 border rounded-xs mb-12 bg-white shadow-sm day-container-v2">
            <div class="border-end bg-light bg-opacity-10 d-flex flex-column align-items-center justify-content-center py-12 px-4" style="width: 130px; flex: none;">
              <div class="tour-day-info w-100 d-flex flex-column align-items-center justify-content-center">
                <div class="date text-xs mb-8 text-dark fw-bold text-center lh-1 text-nowrap px-16" style="font-size: 10px;">
                  {{ getDateFormatted(index) }}
                </div>

                <div
                  class="day-circle d-flex align-items-center justify-content-center fw-bold mb-8 shadow-sm"
                  style="width: 40px; height: 40px; font-size: 18px; border-radius: 50%; border: 2px solid #000000 !important; color: #000000 !important; background: transparent; flex: none;"
                >
                  {{ index + 1 }}
                </div>

                <v-checkbox
                  :id="`tour-day-${element.id}-stage`"
                  :is-disabled="isTourDayUpdating || isSold"
                  :label="$t('tourBuilder.stage')"
                  :left="false"
                  :modelValue="element.is_stage"
                  :right="true"
                  class="mb-0 fw-bold justify-content-center checkbox-compact"
                  style="font-size: 11px; --uq-form-check-input-width: 1em;"
                  for-key="isAllDayActivated"
                  @update:modelValue="updateCaseDayType($event, element)"
                />
              </div>
            </div>

            <div class="col">
              <div class="tour-day-info p-4 h-100 d-flex flex-column justify-content-center">
                <div class="d-flex align-items-start">
                  <div class="tour-day-info-right w-100">
                    <template v-if="element.is_stage">
                      <v-input-address
                        id="tour-day-origin-search"
                        ref="tour-day-origin-search-ref"
                        :disabled-input="isTourDayUpdating || isSold"
                        :get-address="true"
                        :get-radius-bounds="true"
                        :label="$t('general.shared.origin')"
                        label-span-class="bg-white"
                        :radius-bounds="20000"
                        :value="searchLocation(element.origin_id)"
                        class="col-12 mb-0 form-control-compact-v2"
                        @address="createLocation(element, $event)"
                        @input="updateCaseDayOrigin($event, element)"
                      />

                      <v-input-address
                        id="tour-day-destination-search"
                        ref="tour-day-destination-search-ref"
                        :disabled-input="isTourDayUpdating || isSold"
                        :get-address="true"
                        :get-radius-bounds="true"
                        :label="$t('general.shared.destination')"
                        label-span-class="bg-white"
                        :radius-bounds="20000"
                        :value="searchLocation(element.destination_id)"
                        class="col-12 mb-0 form-control-compact-v2"
                        @address="createLocation(element, $event, false)"
                        @input="updateCaseDayDestination($event, element)"
                      />
                      
                      <div class="mt-12 d-flex align-items-center justify-content-between">
                        <div v-if="element.is_stage" class="text-dark fw-bold" style="font-size: 10px;">
                          Distance: {{ element.stage_length ?? element.stageLength }} km
                        </div>

                        <div class="d-flex align-items-center cursor-pointer px-8 py-4 border rounded bg-primary text-white shadow-xs ms-8" style="font-size: 11px;" @click="
                            openOffCanvas('the-case-pax-details-off-canvas');
                            openDayId = isSold ? null : element.id;
                            dayPosition = index + 1;
                          ">
                          <v-icon icon="group" style="font-size: 14px;" class="text-white me-4" />
                          <span class="fw-bold">{{ element.paxes.length }}</span>
                        </div>
                      </div>
                    </template>

                    <template v-else>
                      <v-input-address
                        id="tour-day-location-search"
                        ref="tour-day-location-search-ref"
                        :disabled-input="isTourDayUpdating || isSold"
                        :get-address="true"
                        :get-radius-bounds="true"
                        :label="$t('general.shared.location')"
                        label-span-class="bg-white"
                        :radius-bounds="20000"
                        :value="searchLocation(element.origin_id)"
                        class="col-12 mb-0 form-control-compact-v2"
                        @address="createLocation(element, $event)"
                        @input="updateCaseDayOrigin($event, element)"
                      />

                      <div class="mt-12 d-flex align-items-center justify-content-between">
                        <div />
                        <div class="d-flex align-items-center cursor-pointer px-8 py-4 border rounded bg-primary text-white shadow-xs ms-8" style="font-size: 11px;" @click="
                            openOffCanvas('the-case-pax-details-off-canvas');
                            openDayId = isSold ? null : element.id;
                            dayPosition = index + 1;
                          ">
                          <v-icon icon="group" style="font-size: 14px;" class="text-white me-4" />
                          <span class="fw-bold">{{ element.paxes.length }}</span>
                        </div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-8 bg-white">
              <div class="d-flex justify-content-between h-100 align-items-center p-4 ps-16">
                <the-case-day-item-draggable
                  :agency="agency"
                  :can-add-item="
                    (!element.is_stage && !!element.origin_id) ||
                    (element.is_stage && !!element.origin_id && !!element.destination_id)
                  "
                  :client-tour-id="clientTour.id"
                  :list="element.items"
                  :tour-day-id="element.id"
                  :tour-id="tourId"
                  :tour-mapped="tourMapped"
                  :updating-tour="updatingTour"
                  :is-sold="isSold"
                  type="tour-day-item"
                  @emit-reload-tour="$emit('reloadTour')"
                  @open-item-details-off-canvas="$emit('openItemDetailsOffCanvas', $event)"
                  @close-item-details-off-canvas="$emit('closeItemDetailsOffCanvas', $event)"
                  @move-item-to-another-day="$emit('moveItemToAnotherDay', $event)"
                />

                <div class="d-flex flex-column">
                  <v-button
                    :disabled="updatingTour || isSold"
                    class="btn-day p-4 mb-12"
                    size="sm"
                    variant="icon"
                    @click="$emit('reloadTour')"
                  >
                    <span class="icon icon-refresh text-lg text-primary" />
                  </v-button>

                  <v-button
                    :disabled="updatingTour || isSold"
                    class="btn-day p-4 mb-12"
                    size="sm"
                    variant="icon"
                    @click="removeDay(element)"
                  >
                    <v-icon icon="trash" size="lg" />
                  </v-button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <the-case-pax-details-off-canvas
          v-if="offCanvasToShow === 'the-case-pax-details-off-canvas' && openDayId === element.id"
          :case-day="element"
          :day-position="dayPosition"
          :open-day-id="openDayId"
          @closed="
            closeOffCanvas();
            openDayId = null;
            dayPosition = 0;
          "
          @emit-reload-case="$emit('reloadTour')"
        />
      </div>
    </template>
  </draggable>
</template>

<script setup lang="ts" name="TheCaseDayDraggable">
import { ref, computed, onMounted, PropType } from "vue";
import Vuedraggable from "vuedraggable";
import { VCheckbox } from "@uniqoders/form";
import { DateTime } from "luxon";
import { TYPE, useToast } from "vue-toastification";
import TheCaseDayItemDraggable from "@/components/case/V2/TheCaseDayItemDraggable.vue";
import TheCasePaxDetailsOffCanvas from "@/components/case/V2/TheCasePaxDetailsOffCanvas.vue";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import ClientTour from "@/api/objects/ClientTour";
import api from "@/api";
import VInputAddress from "@/components/vendor/basic/form/VInputAddress.vue";
import Location from "@/api/objects/Location";
import useOffCanvasUtils from "@/helpers/OffCanvasUtils";
import { useI18n } from "vue-i18n";
import { getCurrentInstance } from "vue";

const draggable = Vuedraggable;

const emit = defineEmits([
  "dayMoved",
  "emitAddDay",
  "reloadTour",
  "reloadClientTour",
  "openItemDetailsOffCanvas",
  "closeItemDetailsOffCanvas",
  "openDayInformationOffCanvas",
  "moveItemToAnotherDay",
]);

const props = defineProps({
  modelValue: {
    type: Array as PropType<Record<string, unknown>[]>,
    required: false,
  },
  type: {
    type: String,
    required: false,
    default: "",
  },
  clientTour: {
    type: Object as PropType<ClientTour>,
    required: true,
  },
  tourId: {
    type: Number,
    required: true,
  },
  agency: {
    type: String,
    required: true,
  },
  updatingTour: {
    type: Boolean,
    required: false,
    default: false,
  },
  initDate: {
    type: String,
    required: true,
    default: "",
  },
  endDate: {
    type: String,
    required: true,
    default: "",
  },
  currentTourDay: {
    type: [String, Number] as PropType<string | number>,
    required: false,
    default: "",
  },
  tourMapped: {
    type: Object as PropType<Record<string, unknown>>,
    required: true,
  },
  isSold: {
    type: Boolean,
    required: false,
    default: false,
  }
});

const { t } = useI18n();
const toast = useToast();
const instance = getCurrentInstance();
const $modal = instance?.appContext.config.globalProperties.$modal;
const $toast = instance?.appContext.config.globalProperties.$toast;
const { openOffCanvas, closeOffCanvas, offCanvasToShow } = useOffCanvasUtils();

const isStage = ref<boolean>(false);
const locations = ref<Location[]>([]);
const isTourDayUpdating = ref<boolean>(false);
const paxError = ref<string>("");
const openDayId = ref(null);
const dayPosition = ref<number>(0);

const dragOptions = computed(() => ({
  animation: 0,
  group: "tour-day",
  disabled: false,
  ghostClass: "ghost",
}));

onMounted(async () => {
  await loadLocations();
});

const loadLocations = async (): Promise<void> => {
  locations.value = await api.location.all();
};

// ✅ search by location id
const searchLocation = (locationId: number): string => {
  const locationName = locations.value.find((loc) => loc.id === locationId);
  return locationName ? locationName.name : "";
};

const getDate = (date: string): string => {
  const dateISO = DateTime.fromISO(date);
  return dateISO.toFormat("ccc, dd MMMM yyyy");
};

const getDateFormatted = (index: number): string => {
  if (!props.initDate) return "";
  const start = DateTime.fromISO(props.initDate);
  const date = start.plus({ days: index });
  return date.toFormat("ccc, dd MMM yyyy");
};

const change = (event: any): void => {
  const caseDay = event.moved.element;

  emit("dayMoved", caseDay, event.moved.newIndex + 1);
};

// ✅ add day
const addDay = async (tourDayDate: string): Promise<void> => {
  emit("emitAddDay", tourDayDate);
};

// ✅ remove all items
const removeAllItems = async (caseDay: Record<string, any>): Promise<void> => {
  caseDay?.case_day_items?.forEach((item: Record<string, any>) => {
    api.case.caseDayItemRemove(props.agency, caseDay.case_id, caseDay.id, item.id);
  });
};
// ✅ remove day (with modal confirm)
const removeDay = async (caseDay: Record<string, any>): Promise<void> => {
  await $modal?.delete({
    title: t("tourBuilder.deleteTourDayTitle"),
    text: t("tourBuilder.deleteTourDayText", { dayNumber: caseDay.order, date: getDate(caseDay.date.toString()) }),
    deleteButtonText: t("general.button.delete"),
    rightButtonClasses: "btn-tertiary",
    deleteButtonCallback: () => doRemoveDay(caseDay),
  });
};

const doRemoveDay = async (caseDay: Record<string, any>): Promise<void> => {
  const loadingToast = $toast?.loading(t("general.button.deleting"), "toast-remove-tour-day");

  try {
    await removeAllItems(caseDay);

    await api.case.caseDayDelete(props.agency, caseDay.case_id, caseDay.id);

    emit("reloadTour");
    emit("reloadClientTour");

    loadingToast?.update("toast-remove-tour-day", {
      content: t("tourBuilder.dayDeleted"),
      options: {
        type: TYPE.SUCCESS,
        icon: "icon icon-check-circle-fill",
        timeout: 5000,
      },
    });
  } catch (e: any) {
    loadingToast?.update("toast-remove-tour-day", {
      content: t("tourBuilder.dayDeleteError"),
      options: {
        type: TYPE.ERROR,
        icon: "icon icon-exclamation-triangle-fill",
        timeout: 5000,
      },
    });

    console.error(e?.response?.data);

    toast.error(e?.response?.data?.message || "Error deleting tour day");
  }
};

// ✅ updateCaseDayLength
const updateCaseDayLength = async (tourDay: Record<string, any>): Promise<void> => {
  if ((!tourDay.stageLength && tourDay.stageLength === 0) || (!!tourDay.stageLength && tourDay.stageLength >= 0)) {
    await updateCaseDay(tourDay);
  }
};

// ✅ createLocation
const createLocation = async (
  caseDay: Record<string, any>,
  address: Record<string, any>,
  isOrigin = true
): Promise<void> => {
  const location = await api.location.create({
    name: address.name,
    provider_id: address.placeId,
    lat: address.lat,
    lng: address.lng,
    country: address.country.longName,
    province: address.regionProvince.longName,
    state: address.stateCommunity.longName,
  });

  if (isOrigin) {
    caseDay.origin_id = location.id;
  } else {
    caseDay.destination_id = location.id;
  }

  await updateCaseDay(caseDay);
};

// ✅ setIsStage
const setIsStage = (tourDayType: string): boolean => tourDayType === "stage";

// ✅ updateCaseDayType
const updateCaseDayType = async (checkboxValue: boolean, caseDay: Record<string, any>): Promise<void> => {
  if (!isTourDayUpdating.value) {
    caseDay.is_stage = checkboxValue;
    await updateCaseDay(caseDay);
  }
};

const updateCaseDayOrigin = async (value: string, caseDay: Record<string, any>): Promise<void> => {
  if (!value) {
    caseDay.origin_id = null;
    await updateCaseDay(caseDay);
  }
};

// ✅ update tour day destination
const updateCaseDayDestination = async (value: string, caseDay: Record<string, any>): Promise<void> => {
  if (!value) {
    caseDay.destination_id = null;
    await updateCaseDay(caseDay);
  }
};

// ✅ update tour day pax
const updateCaseDayPax = async (caseDay: Record<string, any>): Promise<void> => {
  if (!caseDay.pax) {
    paxError.value = t("tourBuilder.paxErrorMessage");
  } else {
    paxError.value = "";
    await updateCaseDay(caseDay);
  }
};

// ✅ update tour day (main function)
const updateCaseDay = async (caseDay: Record<string, any>): Promise<void> => {
  if (!isTourDayUpdating.value) {
    try {
      isTourDayUpdating.value = true;

      await api.case.caseDayUpdate(props.agency, caseDay.case_id, caseDay.id, {
        is_stage: caseDay.is_stage,
        origin_id: caseDay.origin_id,
        destination_id: caseDay.destination_id,
      });

      isTourDayUpdating.value = false;
    } catch (e: any) {
      isTourDayUpdating.value = false;

      console.error(e?.response?.data);

      toast.error(e?.response?.data?.message || "Failed to update tour day");
    }
  }
};
</script>

<style lang="scss">
.btn-add-plus {
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
  min-height: 20px !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  border: 0 !important;
}

.add-day-wrapper {
  height: 24px;
}

.btn-add-day-hover {
  opacity: 0;
  visibility: hidden;
}

.add-day-wrapper:hover .btn-add-day-hover {
  opacity: 1;
  visibility: visible;
}

.day-container-v2 {
  border-color: #F4F4F4 !important;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08) !important;
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}

.day-container-v2:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12) !important;
}

.form-control-compact-v2 :deep(.form-control) {
  padding-top: 1px !important;
  padding-bottom: 1px !important;
  min-height: 24px !important;
  font-size: 0.75rem !important;
}

.form-control-compact-v2 :deep(.label) {
  font-size: 0.7rem !important;
  font-weight: 700;
  text-transform: uppercase;
  color: #6c757d;
  margin-bottom: 0;
  transform: translate(0, 45%);
}

.form-control-compact-v2 :deep(.label span) {
  background-color: #f8f9fa !important;
}

:deep(.vs__selected),
:deep(.vs__dropdown-option) {
  font-size: 0.8rem !important;
}

:deep(.v-form-label),
:deep(.form-check-label) {
  font-size: 0.75rem !important;
}

.flip-list-move {
  transition: transform 0.5s;
}

.no-move {
  transition: transform 0s;
}

.list-group {
  min-height: 20px;
}

.list-enter-from {
  opacity: 0;
  transform: scaleY(0.25);
}

.list-enter-active,
.list-leave-active {
  transition: all 1s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-leave-to {
  opacity: 0;
  transform: scaleY(0);
}

.list-move {
  transition: all 1s cubic-bezier(0.55, 0, 0.1, 1);
}

.list-leave-active {
  position: relative;
}

.list-group-item {
  display: block !important;
  padding: 0 !important;
  border: none !important;
  border-radius: 0 !important;
  width: 100% !important;
}
</style>
